/**
 * Engine sound for every car on the road.
 *
 * Three recordings of a real Ka (`public/sounds/`, converted by
 * `scripts/convert-sounds.sh`): an ignition one-shot, a continuous engine loop whose pitch
 * and volume follow the car, and a skid used for the handbrake, for heavy braking, and now
 * for the same two things inferred on remote cars.
 *
 * Two rules shape everything below.
 *
 * **It is allowed to fail.** No `AudioContext`, a blocked autoplay policy, a 404, a codec
 * the browser will not decode — every one of those has to leave a silent but otherwise
 * completely normal game behind, with nothing on the console. Audio is the least important
 * thing on screen and it does not get to break the race.
 *
 * **Everyone, but positionally.** This used to be local-player-only, on the stated grounds
 * that seven detuned copies of one 2.6 s sample is a mush. That objection was real and is
 * still the reason the code below exists: each remote car gets its own `PannerNode` fed
 * from an `AudioContext.listener` driven by the camera, so a car passing on your left
 * sounds like it and one three streets away is close to silent — hard distance rolloff
 * does most of the work of separating the voices, a small per-car detune (`detuneFor`)
 * handles the case where two cars are side by side at the same speed and would otherwise
 * phase-lock, and a hard cap (`MAX_AUDIBLE_REMOTES`) is the backstop for a full grid at a
 * standing start, where rolloff alone would still leave seven engines close enough to hear
 * at once. See the remote section below for what was actually measured.
 *
 * Remote cars report only position and signed speed at ~20 Hz — no throttle, no handbrake,
 * no impact — so braking and wheelspin are *inferred* from consecutive samples rather than
 * known. That inference is honest about being an inference: it is one threshold on
 * deceleration between two real network samples, not a reconstruction of what the other
 * player's pedals were doing.
 *
 * Everything that is *not* a car — the weather, the city, a sound for each thing that
 * happens to a player — lives in `ambience.ts`, which hangs off this file's master gain so
 * that the mute key and the volume ramp cover both. This file only ever grows clips that
 * every install ships; that one is built around files that mostly are not there yet.
 */

import { musicPlayer, type MusicDebug } from './music.js';
import { ambience, type AmbienceDebug } from './ambience.js';

const BASE = '/sounds';

/** Files, without extension — the format is chosen at runtime. */
const CLIPS = {
  start: 'ka_start',
  drive: 'ka-drive',
  brake: 'ka-hardbrake',
  countdown: 'countdown',
  honk: 'honk',
} as const;
type ClipName = keyof typeof CLIPS;

/**
 * Clips that ship in something other than the two formats `orderedFormats` picks between.
 *
 * The engine sounds were transcoded to ogg and m4a because they are long and are streamed
 * on every race; the horn is 36 KB of wav, which every browser decodes and nothing gains
 * from converting. Listing the exception here beats probing for two files that are not
 * there on every load.
 */
const CLIP_FORMATS: Partial<Record<ClipName, string[]>> = {
  honk: ['wav'],
};

const MUTE_KEY = 'osm-racer.muted';
/** Deliberately modest: the game is not the only thing making noise on the machine. */
const MASTER_VOLUME = 0.5;

// --- the engine loop --------------------------------------------------------

/**
 * Where the engine loop splices, measured off `ka-drive.wav` offline: the pair of points
 * ~0.27 s and ~2.18 s in whose surrounding waveforms match most closely. Looping the whole
 * 2.58 s buffer instead puts a 0.364 step between its last sample and its first, which is
 * a click every 2.6 seconds.
 *
 * These are *hints*, not constants, because they do not survive the trip intact. AAC adds
 * priming samples — the .m4a decodes to 428 frames more than the source — and Web Audio
 * resamples to the output device's rate, so `refineLoop` re-finds them in the buffer the
 * browser actually handed us. Measured across the source WAV, both encodings and both 44.1
 * and 48 kHz, that lands within 0.1 ms of the same place every time.
 */
const LOOP_HINT_START = 0.2662;
const LOOP_HINT_END = 2.1819;
/** How far from a hint to look, seconds. Comfortably more than the AAC priming shift. */
const LOOP_SEARCH = 0.03;
/** Waveform either side of the splice that has to match, seconds. */
const LOOP_MATCH = 0.002;

/**
 * Flat-out speed in m/s. This is the *measured* terminal speed on a motorway (157 km/h),
 * not `DEFAULT_TUNING.topSpeed` — the tuning's 54 m/s is a figure the drag curve never
 * lets the car reach, and pitching the engine against it would leave the top of the range
 * unused.
 */
const TOP_SPEED = 44;

/** Playback rate at a standstill and flat out. */
const RATE_IDLE = 0.72;
const RATE_MAX = 1.7;
/**
 * Below 1 so the rate climbs quickly out of a standstill and then flattens. A straight
 * line across a 0-157 km/h range either barely moves in town or turns into a mosquito at
 * the top; this spends most of the pitch range where most of the driving happens.
 */
const RATE_CURVE = 0.6;

/** Engine gain = idle + throttle + a little for speed alone, so coasting is not silent. */
const GAIN_IDLE = 0.2;
const GAIN_THROTTLE = 0.5;
const GAIN_SPEED = 0.22;

/**
 * Seconds between horns, per car.
 *
 * Long enough that leaning on the key is one honk rather than a chainsaw, short enough for
 * two quick taps to read as two. Applied to remote horns as well as your own, so somebody
 * with a modified client cannot make a noise here that they could not make there.
 */
const HONK_COOLDOWN = 0.35;

/** Seconds. Long enough that the skid cannot machine-gun, short enough to feel reactive. */
const SKID_COOLDOWN = 0.38;

// --- remote cars --------------------------------------------------------------

/**
 * `PannerNode` distance model. `refDistance` is full volume — 6 m, about a lane and its
 * shoulder, close enough that "passing on your left" reads as passing rather than merely
 * nearby. `rolloffFactor` is chosen against the exponential model's own falloff curve
 * (`(distance / refDistance) ^ -rolloffFactor`) rather than guessed: at 2.4 a car is down
 * about 25 dB by 20 m and about 44 dB — practically silent — by 50 m, so "three streets
 * away" (well over 100 m in every shipped region) never has to be handled as a special
 * case, it is just already past the noise floor.
 */
const REMOTE_REF_DISTANCE = 6;
const REMOTE_ROLLOFF = 2.4;
/**
 * Hard cutoff, metres. The exponential rolloff above already makes anything past ~50 m
 * negligible; this exists so a far car costs nothing rather than costing an
 * imperceptibly-quiet `PannerNode` update, and so `finalizeRemoteAudibility` has a clean
 * boolean rather than a threshold on a number that is never exactly zero.
 */
const REMOTE_SILENT_BEYOND = 140;
/**
 * How many remote engines are allowed to be audible at once. The rolloff above already
 * separates almost every situation in normal racing — cars spread out along a route — but
 * a grid start puts up to seven cars within a few car-lengths of each other and of the
 * listener, which is exactly the "mush" the header comment used to cite as the reason not
 * to do this at all. Capping to the four nearest keeps the grid readable as "several
 * engines nearby" instead of one texture, while the ones it silences are, at a standing
 * start, close enough together that losing them costs little separation anyway.
 */
const MAX_AUDIBLE_REMOTES = 4;
/**
 * Per-car pitch offset, in cents, so two remote cars at the same indicated speed do not
 * play the identical sample at the identical rate — which, started at different times but
 * looping at the same rate, drifts in and out of phase slowly enough to sound like a
 * beating hum rather than two engines. `RATE_IDLE`..`RATE_MAX` already spans about 1490
 * cents over the car's whole speed range, so ±20 cents is a small fraction of the pitch
 * change a change in speed already produces — enough to decorrelate two voices, not enough
 * to read as "that car's engine sounds wrong".
 */
const DETUNE_SPREAD = 20;
/**
 * Deceleration between two consecutive network samples that reads as braking hard rather
 * than coasting off the throttle, m/s². Grounded in the car's own tuning
 * (`DEFAULT_TUNING` in `car.ts`): engine braking alone is 5 m/s², full lock is 26. 12 sits
 * comfortably above the first and comfortably below the second, so it catches "braking
 * hard" without also firing on every lift of the throttle.
 */
const REMOTE_SKID_DECEL = 12;
/** Floor so a car that is essentially stopped — parking, or GPS-style jitter at the grid —
 *  never registers as a lock-up. Matches the speed floor the local skid check uses. */
const REMOTE_SKID_MIN_SPEED = 7;
/** Engine note for a remote car is speed alone — there is no throttle telemetry to add a
 *  load term the way the local engine's gain does. */
const REMOTE_GAIN_IDLE = 0.18;
const REMOTE_GAIN_SPEED = 0.55;
/** How quickly a voice's audibility (in or out of the cap, in or out of range) fades, as
 *  an exponential rate. Slow enough that cars trading places near the edge of the cap
 *  crossfade rather than click. */
const REMOTE_AUDIBILITY_RATE = 6;

// --- the start signal -------------------------------------------------------

/**
 * `countdown.webm` is the standard racing start: three 0.49 s beeps exactly one second
 * apart, then a 0.97 s tone. The thing that has to line up with the green light is the
 * *last* tone, not the end of the file — so the clip is scheduled backwards from the
 * server's start time, and is happy to begin mid-file if the message arrived late.
 *
 * Where that tone begins is measured in the decoded buffer rather than hard-coded, for
 * the same reason the engine's loop points are: AAC priming and Web Audio's resampling
 * both move it by a few milliseconds, and a different recording would move it entirely.
 */
const CUE_WINDOW = 0.005;
/** Loud/quiet thresholds relative to the clip's peak, with hysteresis so a tone that
 *  dips briefly on its decay doesn't read as two separate beeps. */
const CUE_ON_DB = -14;
const CUE_OFF_DB = -16;
/** Beeps are a second apart and half a second long, so these separate signal from wobble. */
const CUE_MERGE_GAP = 0.25;
const CUE_MIN_LENGTH = 0.1;

export interface EngineFrame {
  /** Absolute forward speed, m/s. */
  speed: number;
  /** Throttle axis as sampled from the controls, -1..1. */
  throttle: number;
  handbrake: boolean;
  /** Impact speed from the last physics step, m/s; 0 if nothing was hit. */
  impact: number;
  /** False during the countdown, after the finish, and away from the game screen. */
  racing: boolean;
}

/** One remote car's positional voice: an engine loop and an occasional skid one-shot, both
 *  fed through the same `PannerNode` so they always come from the same place. */
interface RemoteVoice {
  panner: PannerNode;
  /** Gain the engine loop is mixed at before the panner — the speed-driven "how loud is
   *  this engine" term, independent of distance. */
  engineGain: GainNode;
  /** Gain *after* the panner — distance-cutoff and cap audibility, smoothed so a car
   *  crossing the cap boundary or the silence radius fades rather than clicks. */
  voiceGain: GainNode;
  engineSource: AudioBufferSourceNode | null;
  detuneCents: number;
  rate: number;
  gain: number;
  x: number;
  y: number;
  z: number;
  /** The last *real* (network) speed sample and when it landed, for the deceleration
   *  check — `updateRemote` is called every render frame, but `speed` only actually
   *  changes at the ~20 Hz the server reports it, and the check only means anything
   *  between two real samples. */
  lastSpeed: number;
  lastSampleAt: number;
  lastSkidAt: number;
  lastHonkAt: number;
  /** Cached for `debug()`, so a test can read them without recomputing. */
  lastDistance: number;
  lastAudible: boolean;
}

/** One remote car's audio state, for the Playwright suite — see `RemoteVoice`. */
export interface RemoteAudioDebug {
  id: string;
  x: number;
  y: number;
  z: number;
  /** Metres from the listener, as last computed by `finalizeRemoteAudibility`. */
  distance: number;
  /** The voice's post-panner gain, 0..1 — what actually reaches the speakers. */
  gain: number;
  detuneCents: number;
  /** Within both the distance cutoff and the audible-count cap. */
  audible: boolean;
}

/** Read by the Playwright suite, which has no ears — see `getAudio` in `main.ts`. */
export interface AudioDebug {
  supported: boolean;
  /** The container the last clip decoded from — normally the same one for all three. */
  format: string | null;
  loaded: ClipName[];
  contextState: string | null;
  running: boolean;
  muted: boolean;
  playbackRate: number;
  engineGain: number;
  loopStart: number;
  loopEnd: number;
  skids: number;
  honks: number;
  starts: number;
  countdowns: number;
  /** Seconds into the start signal where its final tone begins; null until measured. */
  countdownCue: number | null;
  /** One entry per remote car currently tracked, nearest first. */
  remotes: RemoteAudioDebug[];
  /** The position last handed to `context.listener`; null before the first frame. */
  listener: { x: number; y: number; z: number } | null;
  music: MusicDebug;
  /** The weather, the city and the event cues — see `ambience.ts`. */
  env: AmbienceDebug;
}

class CarAudio {
  private context: AudioContext | null = null;
  private master: GainNode | null = null;
  private engineGain: GainNode | null = null;
  private engine: AudioBufferSourceNode | null = null;

  private readonly buffers = new Map<ClipName, AudioBuffer>();
  private format: string | null = null;
  private loading = false;
  private failed = false;

  private isMuted = readMuted();
  private rate = RATE_IDLE;
  private gain = 0;
  private loop = { start: 0, end: 0 };
  private lastSkidAt = -Infinity;

  private countdownSource: AudioBufferSourceNode | null = null;
  /** Seconds into the buffer where the green light lands. Measured once, lazily. */
  private countdownCue: number | null = null;
  private countdownToken = 0;

  /** Counters purely so a test can assert a one-shot actually fired. */
  private skids = 0;
  private honks = 0;
  private lastHonkAt = -Infinity;
  private starts = 0;
  private countdowns = 0;

  private readonly remoteVoices = new Map<string, RemoteVoice>();
  private listenerPos: { x: number; y: number; z: number } | null = null;

  get muted(): boolean {
    return this.isMuted;
  }

  /**
   * Called from the first real click or key press. Browsers refuse to start an
   * `AudioContext` outside a user gesture, and constructing one anyway earns a console
   * warning and a context stuck in `suspended` forever.
   */
  unlock(): void {
    if (this.failed) return;
    try {
      if (!this.context) {
        const Ctor =
          window.AudioContext ??
          (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
        if (!Ctor) {
          this.failed = true;
          return;
        }
        this.context = new Ctor();
        this.master = this.context.createGain();
        this.master.gain.value = this.isMuted ? 0 : MASTER_VOLUME;
        this.master.connect(this.context.destination);
        this.engineGain = this.context.createGain();
        this.engineGain.gain.value = 0;
        this.engineGain.connect(this.master);
      }
      // The environment shares this context and hangs off the same master, so it is muted,
      // ramped and torn down with everything else. Idempotent — `unlock` runs on every
      // gesture until the context is actually running.
      if (this.master) ambience.attach(this.context, this.master);
      // `resume` rejects if the gesture has already gone stale; that is survivable.
      void this.context.resume().catch(() => undefined);
      void this.load();
    } catch {
      this.failed = true;
    }
  }

  setMuted(muted: boolean): void {
    this.isMuted = muted;
    try {
      localStorage.setItem(MUTE_KEY, muted ? '1' : '0');
    } catch {
      // Private browsing. The setting simply will not persist.
    }
    // `M` is one key for the whole page, and music is a second, entirely separate audio
    // element — this is the one line that keeps them in sync.
    musicPlayer.setMuted(muted);
    if (this.master && this.context) {
      // A short ramp rather than a jump, or muting mid-corner is its own click.
      const now = this.context.currentTime;
      this.master.gain.cancelScheduledValues(now);
      this.master.gain.setValueAtTime(this.master.gain.value, now);
      this.master.gain.linearRampToValueAtTime(muted ? 0 : MASTER_VOLUME, now + 0.06);
    }
  }

  toggleMuted(): boolean {
    this.setMuted(!this.isMuted);
    return this.isMuted;
  }

  /** The ignition, once, as the lights go green. */
  playStart(): void {
    if (this.playOnce('start', 1)) this.starts++;
    // Green light: music can come back up, the engine and the ignition carry the moment.
    musicPlayer.unduck();
  }

  /**
   * Your own horn.
   *
   * Returns false when it was refused, so the caller knows not to tell the server about a
   * press nobody heard. The cooldown is here rather than in the key handler because it has
   * to apply to a held key, a tapped button and a touch control alike, and because the
   * thing being rate-limited is a sound.
   */
  playHonk(): boolean {
    const context = this.context;
    if (!context) return false;
    if (context.currentTime - this.lastHonkAt < HONK_COOLDOWN) return false;
    this.lastHonkAt = context.currentTime;
    if (this.playOnce('honk', 0.7)) this.honks++;
    return true;
  }

  /**
   * Somebody else's horn, from wherever their car is.
   *
   * Routed into that car's existing `PannerNode`, so it arrives with the same position,
   * distance falloff and stereo placement as their engine — which is the entire point of a
   * horn: it tells you *where* they are, not merely that they exist. A car with no voice
   * yet (out of the audible set, or a snapshot away from having one) is simply not heard,
   * which is the same rule its engine is already following.
   */
  playRemoteHonk(id: string): void {
    const context = this.context;
    const voice = this.remoteVoices.get(id);
    const buffer = this.buffers.get('honk');
    if (!context || !voice || !buffer) return;
    if (context.currentTime - voice.lastHonkAt < HONK_COOLDOWN) return;
    voice.lastHonkAt = context.currentTime;
    try {
      const source = context.createBufferSource();
      source.buffer = buffer;
      const gain = context.createGain();
      gain.gain.value = 1;
      source.connect(gain);
      gain.connect(voice.panner);
      source.start();
      source.onended = () => {
        source.disconnect();
        gain.disconnect();
      };
      this.honks++;
    } catch {
      // A horn that will not play is not worth a word to anyone.
    }
  }

  /**
   * Schedules the start signal so its final tone lands on the green light.
   *
   * `msUntilGo` is measured from now against the local clock, which `beginCountdown` has
   * already reconciled with the server's. Everything after that is arithmetic on the
   * AudioContext clock, so the tone hits its mark regardless of frame timing.
   */
  playCountdown(msUntilGo: number): void {
    // Ducking happens even if the AudioContext itself is unavailable — music runs through
    // a plain <audio> element and does not depend on it (see `music.ts`) — so this comes
    // before the `failed` guard below, which only gates the countdown tone itself.
    musicPlayer.duck();
    if (this.failed) return;
    // The host's click on "Start race" is the gesture that unlocks audio, and the
    // countdown follows it immediately — so this may well be the first sound of the page.
    this.unlock();
    const token = ++this.countdownToken;
    void this.scheduleCountdown(msUntilGo, performance.now(), token);
  }

  private async scheduleCountdown(msUntilGo: number, issuedAt: number, token: number): Promise<void> {
    if (!this.buffers.has('countdown')) await this.load();
    if (token !== this.countdownToken) return;

    const context = this.context;
    const buffer = this.buffers.get('countdown');
    if (!context || !this.master || !buffer) return;

    if (this.countdownCue === null) {
      this.countdownCue = findFinalTone(buffer.getChannelData(0), buffer.sampleRate);
    }
    const cue = this.countdownCue;

    // Decoding four clips can take a moment on a cold cache, so re-read the clock rather
    // than trusting the figure we were handed before the await.
    const remaining = (msUntilGo - (performance.now() - issuedAt)) / 1000;
    const offset = Math.max(0, cue - remaining);
    // Too late for even the final tone to be a countdown rather than a noise.
    if (offset >= cue + 0.15) return;

    try {
      const source = context.createBufferSource();
      source.buffer = buffer;
      const node = context.createGain();
      node.gain.value = 1;
      source.connect(node);
      node.connect(this.master);
      source.start(context.currentTime + Math.max(0, remaining - cue), offset);
      source.onended = () => {
        source.disconnect();
        node.disconnect();
        if (this.countdownSource === source) this.countdownSource = null;
      };
      this.countdownSource?.stop();
      this.countdownSource = source;
      this.countdowns++;
    } catch {
      // A start signal that will not play is not worth a word to anyone either.
    }
  }

  /** Cuts the start signal short — someone left, or the race was abandoned mid-countdown. */
  stopCountdown(): void {
    this.countdownToken++;
    try {
      this.countdownSource?.stop();
    } catch {
      // Already finished, or never started.
    }
    this.countdownSource = null;
    // Nothing is going to un-duck this on its own if the countdown never reaches a green
    // light — an abandoned race would otherwise leave music quiet for the rest of the
    // session.
    musicPlayer.unduck();
  }

  /** Per frame. Drives the engine loop and fires the skid when it is earned. */
  update(frame: EngineFrame, dt: number): void {
    const context = this.context;
    if (!context || !this.engineGain) return;
    this.ensureEngine();

    const speed = Math.min(TOP_SPEED * 1.1, Math.abs(frame.speed));
    const load = Math.min(1, Math.abs(frame.throttle));
    const fraction = Math.min(1, speed / TOP_SPEED);

    const targetRate = RATE_IDLE + (RATE_MAX - RATE_IDLE) * Math.pow(fraction, RATE_CURVE);
    const targetGain = frame.racing
      ? Math.min(0.95, GAIN_IDLE + GAIN_THROTTLE * load + GAIN_SPEED * fraction)
      : 0;

    // Smoothed in JS rather than with `setTargetAtTime`, which would pile a new automation
    // event onto the param every single frame.
    const k = Math.min(1, dt * 9);
    this.rate += (targetRate - this.rate) * k;
    this.gain += (targetGain - this.gain) * Math.min(1, dt * (targetGain > this.gain ? 6 : 3));

    if (this.engine) this.engine.playbackRate.value = this.rate;
    this.engineGain.gain.value = this.gain;

    if (!frame.racing) return;
    // Handbrake, standing on the brakes from real speed, or a solid hit. The speed floors
    // stop a three-point turn sounding like a rally stage.
    const skid =
      (frame.handbrake && speed > 7) || (frame.throttle < -0.55 && speed > 13) || frame.impact > 7;
    if (skid) this.playSkid(Math.min(1, 0.45 + speed / 34));
  }

  playSkid(gain = 1): void {
    const context = this.context;
    if (!context) return;
    if (context.currentTime - this.lastSkidAt < SKID_COOLDOWN) return;
    this.lastSkidAt = context.currentTime;
    if (this.playOnce('brake', gain)) this.skids++;
  }

  /**
   * Race over, or the game screen went away. Fades the engine out but keeps it loaded.
   * Cuts every remote voice immediately, not smoothed — `Game.stop()` has already stopped
   * calling `updateRemote`/`finalizeRemoteAudibility`, so nothing would otherwise be left
   * to bring the gain back down and the grid would hum away under the lobby screen, same
   * as the local engine would without this method existing at all.
   */
  silence(): void {
    this.gain = 0;
    if (this.engineGain) this.engineGain.gain.value = 0;
    for (const voice of this.remoteVoices.values()) {
      voice.gain = 0;
      voice.engineGain.gain.value = 0;
      voice.voiceGain.gain.value = 0;
    }
  }

  debug(): AudioDebug {
    return {
      supported: !this.failed,
      format: this.format,
      loaded: [...this.buffers.keys()],
      contextState: this.context?.state ?? null,
      running: this.engine !== null,
      muted: this.isMuted,
      playbackRate: this.rate,
      engineGain: this.gain,
      loopStart: this.loop.start,
      loopEnd: this.loop.end,
      skids: this.skids,
      honks: this.honks,
      starts: this.starts,
      countdowns: this.countdowns,
      countdownCue: this.countdownCue,
      remotes: [...this.remoteVoices.entries()].map(([id, voice]) => ({
        id,
        x: voice.x,
        y: voice.y,
        z: voice.z,
        distance: voice.lastDistance,
        gain: voice.voiceGain.gain.value,
        detuneCents: voice.detuneCents,
        audible: voice.lastAudible,
      })),
      listener: this.listenerPos,
      music: musicPlayer.debug(),
      env: ambience.debug(),
    };
  }

  // -------------------------------------------------------------------------
  // remote cars
  // -------------------------------------------------------------------------

  /**
   * Positions the `AudioContext.listener` from the camera. Takes a quaternion rather than
   * a `THREE.Camera` so this file stays free of a three.js import — the rest of it already
   * has no rendering dependency, and there is no reason positional audio should be the one
   * thing that changes that.
   */
  updateListener(x: number, y: number, z: number, qx: number, qy: number, qz: number, qw: number): void {
    const context = this.context;
    if (!context) return;
    this.listenerPos = { x, y, z };
    try {
      const listener = context.listener;
      const forward = rotateVector(qx, qy, qz, qw, 0, 0, -1);
      const up = rotateVector(qx, qy, qz, qw, 0, 1, 0);
      // The AudioParam-based listener API is what every browser we support ships, but
      // `setPosition`/`setOrientation` are still there on older engines — falling back
      // rather than throwing costs three lines and means positional audio degrades to
      // "off" instead of "the whole update method throws every frame".
      if (listener.positionX) {
        listener.positionX.value = x;
        listener.positionY.value = y;
        listener.positionZ.value = z;
        listener.forwardX.value = forward.x;
        listener.forwardY.value = forward.y;
        listener.forwardZ.value = forward.z;
        listener.upX.value = up.x;
        listener.upY.value = up.y;
        listener.upZ.value = up.z;
      } else if (listener.setPosition) {
        listener.setPosition(x, y, z);
        listener.setOrientation(forward.x, forward.y, forward.z, up.x, up.y, up.z);
      }
    } catch {
      // Positional audio degrades to non-positional rather than breaking the frame.
    }
  }

  /**
   * Per remote car, per render frame. Moves its `PannerNode`, drives its engine loop from
   * `speed`, and — only when `speed` has actually changed, i.e. a new network sample has
   * landed, not merely a new render frame — checks whether the gap since the last one looks
   * like a lock-up. See `REMOTE_SKID_DECEL`.
   */
  updateRemote(id: string, x: number, y: number, z: number, speed: number, now: number, dt: number): void {
    const context = this.context;
    if (!context) return;
    let voice: RemoteVoice | null = this.remoteVoices.get(id) ?? null;
    if (!voice) voice = this.createRemoteVoice(id);
    if (!voice) return;
    this.ensureRemoteEngine(voice);

    voice.x = x;
    voice.y = y;
    voice.z = z;
    try {
      voice.panner.positionX.value = x;
      voice.panner.positionY.value = y;
      voice.panner.positionZ.value = z;
    } catch {
      // No positional param support; the panner still attenuates by distance to the
      // origin, which is wrong but not silent — see `updateListener`'s own fallback.
    }

    const absSpeed = Math.min(TOP_SPEED * 1.1, Math.abs(speed));
    const fraction = Math.min(1, absSpeed / TOP_SPEED);
    const targetRate = RATE_IDLE + (RATE_MAX - RATE_IDLE) * Math.pow(fraction, RATE_CURVE);
    const targetGain = Math.min(0.85, REMOTE_GAIN_IDLE + REMOTE_GAIN_SPEED * fraction);
    const k = Math.min(1, dt * 9);
    voice.rate += (targetRate - voice.rate) * k;
    voice.gain += (targetGain - voice.gain) * Math.min(1, dt * (targetGain > voice.gain ? 6 : 3));
    if (voice.engineSource) voice.engineSource.playbackRate.value = voice.rate;
    voice.engineGain.gain.value = voice.gain;

    if (speed !== voice.lastSpeed) {
      const elapsed = (now - voice.lastSampleAt) / 1000;
      // Bounded so a stale first sample or a dropped-then-resumed connection cannot read
      // as an enormous, instant deceleration.
      if (elapsed > 0.02 && elapsed < 0.5) {
        const decel = (Math.abs(voice.lastSpeed) - Math.abs(speed)) / elapsed;
        if (decel > REMOTE_SKID_DECEL && Math.abs(voice.lastSpeed) > REMOTE_SKID_MIN_SPEED) {
          this.playRemoteSkid(voice);
        }
      }
      voice.lastSpeed = speed;
      voice.lastSampleAt = now;
    }
  }

  /**
   * Ranks every remote voice by distance to the listener and fades each one to full or to
   * silence depending on whether it is inside `REMOTE_SILENT_BEYOND` *and* among the
   * `MAX_AUDIBLE_REMOTES` nearest. Called once per frame after every `updateRemote`, not
   * from inside it, because the ranking needs everyone's position for this frame first.
   */
  finalizeRemoteAudibility(dt: number): void {
    if (!this.listenerPos) return;
    const { x: lx, y: ly, z: lz } = this.listenerPos;
    const ranked = [...this.remoteVoices.values()]
      .map((voice) => ({ voice, distance: Math.hypot(voice.x - lx, voice.y - ly, voice.z - lz) }))
      .sort((a, b) => a.distance - b.distance);

    const k = Math.min(1, dt * REMOTE_AUDIBILITY_RATE);
    ranked.forEach(({ voice, distance }, index) => {
      const audible = distance <= REMOTE_SILENT_BEYOND && index < MAX_AUDIBLE_REMOTES;
      const target = audible ? 1 : 0;
      voice.voiceGain.gain.value += (target - voice.voiceGain.gain.value) * k;
      voice.lastDistance = distance;
      voice.lastAudible = audible;
    });
  }

  /** A car left the race, or the world it was in did. `AudioBufferSourceNode` cannot be
   *  reused, so this is a real teardown rather than a mute — the alternative is a voice
   *  playing forever for a car nobody can see any more. */
  removeRemote(id: string): void {
    const voice = this.remoteVoices.get(id);
    if (!voice) return;
    this.remoteVoices.delete(id);
    try {
      voice.engineSource?.stop();
    } catch {
      // Already stopped, or never started.
    }
    try {
      voice.engineSource?.disconnect();
      voice.engineGain.disconnect();
      voice.panner.disconnect();
      voice.voiceGain.disconnect();
    } catch {
      // Already disconnected.
    }
  }

  private createRemoteVoice(id: string): RemoteVoice | null {
    const context = this.context;
    if (!context || !this.master) return null;
    try {
      const panner = context.createPanner();
      // Equal-power rather than HRTF: HRTF is tuned for one listener wearing headphones,
      // costs more per source, and with several close-together voices its head-shadow
      // filtering is what turns into the "mush" the header comment used to worry about.
      // Equal-power is the plain stereo pan every racing game actually uses.
      panner.panningModel = 'equalpower';
      panner.distanceModel = 'exponential';
      panner.refDistance = REMOTE_REF_DISTANCE;
      panner.rolloffFactor = REMOTE_ROLLOFF;
      panner.maxDistance = REMOTE_SILENT_BEYOND;

      const engineGain = context.createGain();
      engineGain.gain.value = 0;
      const voiceGain = context.createGain();
      voiceGain.gain.value = 0;
      engineGain.connect(panner);
      panner.connect(voiceGain);
      voiceGain.connect(this.master);

      const voice: RemoteVoice = {
        panner,
        engineGain,
        voiceGain,
        engineSource: null,
        detuneCents: detuneFor(id),
        rate: RATE_IDLE,
        gain: 0,
        x: 0,
        y: 0,
        z: 0,
        lastSpeed: 0,
        lastSampleAt: performance.now(),
        lastSkidAt: -Infinity,
        lastHonkAt: -Infinity,
        lastDistance: Infinity,
        lastAudible: false,
      };
      this.remoteVoices.set(id, voice);
      return voice;
    } catch {
      return null;
    }
  }

  /** Same reasoning as `ensureEngine` for the local car: one looping source for the voice's
   *  lifetime, silent through its gain rather than started and stopped. Retried every frame
   *  until the `drive` buffer is loaded, since a remote can appear before `load()` finishes. */
  private ensureRemoteEngine(voice: RemoteVoice): void {
    if (voice.engineSource || !this.context) return;
    const buffer = this.buffers.get('drive');
    if (!buffer) return;
    try {
      const source = this.context.createBufferSource();
      source.buffer = buffer;
      source.loop = true;
      // Reuses the local engine's own loop points once they exist — same buffer, same
      // splice — rather than recomputing `refineLoop` a second time for identical input.
      // Before that has happened (a remote can appear before the local car has played a
      // single frame), fall back to the whole buffer, same as `refineLoop` itself does.
      const hasLocalLoop = this.loop.end > this.loop.start;
      source.loopStart = hasLocalLoop ? this.loop.start : 0;
      source.loopEnd = hasLocalLoop ? this.loop.end : buffer.duration;
      source.detune.value = voice.detuneCents;
      source.playbackRate.value = voice.rate;
      source.connect(voice.engineGain);
      source.start(0, source.loopStart);
      voice.engineSource = source;
    } catch {
      voice.engineSource = null;
    }
  }

  private playRemoteSkid(voice: RemoteVoice): void {
    const context = this.context;
    const buffer = this.buffers.get('brake');
    if (!context || !buffer) return;
    if (context.currentTime - voice.lastSkidAt < SKID_COOLDOWN) return;
    voice.lastSkidAt = context.currentTime;
    try {
      const source = context.createBufferSource();
      source.buffer = buffer;
      const gain = context.createGain();
      gain.gain.value = 0.8;
      source.connect(gain);
      gain.connect(voice.panner);
      source.start();
      source.onended = () => {
        source.disconnect();
        gain.disconnect();
      };
      this.skids++;
    } catch {
      // A remote skid that will not play is not worth a word to anyone either.
    }
  }

  // -------------------------------------------------------------------------
  // internals
  // -------------------------------------------------------------------------

  /**
   * One looping source for the lifetime of the page, audible only through its gain.
   * `AudioBufferSourceNode` is single-use — it cannot be restarted once stopped — so
   * starting and stopping it per race is a lifecycle bug waiting to happen, and a silent
   * looping node costs nothing.
   */
  private ensureEngine(): void {
    if (this.engine || !this.context || !this.engineGain) return;
    const buffer = this.buffers.get('drive');
    if (!buffer) return;
    try {
      const source = this.context.createBufferSource();
      source.buffer = buffer;
      source.loop = true;
      this.loop = refineLoop(buffer.getChannelData(0), buffer.sampleRate);
      source.loopStart = this.loop.start;
      source.loopEnd = this.loop.end;
      source.playbackRate.value = this.rate;
      source.connect(this.engineGain);
      source.start(0, this.loop.start);
      this.engine = source;
    } catch {
      this.engine = null;
    }
  }

  /** True only if a sound really started, which is what the debug counters report. */
  private playOnce(clip: ClipName, gain: number): boolean {
    const context = this.context;
    const buffer = this.buffers.get(clip);
    if (!context || !this.master || !buffer) return false;
    try {
      const source = context.createBufferSource();
      source.buffer = buffer;
      const node = context.createGain();
      node.gain.value = gain;
      source.connect(node);
      node.connect(this.master);
      source.start();
      source.onended = () => {
        source.disconnect();
        node.disconnect();
      };
      return true;
    } catch {
      // A one-shot that will not play is not worth a word to anyone.
      return false;
    }
  }

  private async load(): Promise<void> {
    if (this.loading || this.buffers.size === Object.keys(CLIPS).length) return;
    this.loading = true;
    const context = this.context;
    if (!context) return;
    // Ordered by what this browser says it prefers, but both are always tried: a
    // `canPlayType` of "maybe" is exactly that.
    const formats = orderedFormats();
    try {
      for (const [name, file] of Object.entries(CLIPS) as [ClipName, string][]) {
        if (this.buffers.has(name)) continue;
        for (const format of CLIP_FORMATS[name] ?? formats) {
          try {
            const response = await fetch(`${BASE}/${file}.${format}`);
            if (!response.ok) continue;
            const bytes = await response.arrayBuffer();
            const buffer = await context.decodeAudioData(bytes);
            this.buffers.set(name, buffer);
            // Only the negotiated formats are worth reporting; a clip with its own fixed
            // one says nothing about what this browser preferred.
            if (!CLIP_FORMATS[name]) this.format = format;
            break;
          } catch {
            // Try the other container; if both fail this clip is simply never heard.
          }
        }
      }
    } finally {
      // Cleared either way, so a later unlock can have another go at whatever is missing.
      this.loading = false;
    }
  }
}

/**
 * Re-finds the loop points in a decoded buffer.
 *
 * `loopStart` goes to the nearest upward zero crossing, and `loopEnd` to whichever *other*
 * upward zero crossing within the search window continues the waveform most like the one
 * after `loopStart`. Constraining both ends to a zero crossing is what actually kills the
 * click: it holds the step across the splice to a few ten-thousandths, about 25 dB under
 * the signal, where searching on waveform similarity alone leaves it twenty times bigger.
 */
export function refineLoop(
  data: Float32Array,
  rate: number,
): { start: number; end: number } {
  const duration = data.length / rate;
  /** Nothing usable: let the browser loop the whole buffer and take the click. */
  const wholeBuffer = { start: 0, end: duration };
  const search = Math.round(LOOP_SEARCH * rate);
  const match = Math.max(1, Math.round(LOOP_MATCH * rate));
  const limit = data.length - match - 1;
  if (limit < match * 8) return wholeBuffer;

  const clamp = (i: number, lo: number, hi: number) => (i < lo ? lo : i > hi ? hi : i);
  const rising = (i: number) => data[i] <= 0 && data[i + 1] > 0;
  // The hints describe the clip that ships. A different, shorter recording gets
  // proportional points instead, so swapping the file degrades to a sane interior loop
  // rather than to `loopEnd <= loopStart`, which silently disables the loop points.
  const fits = LOOP_HINT_END + LOOP_SEARCH < duration;
  const hintStart = clamp(
    Math.round((fits ? LOOP_HINT_START : duration * 0.1) * rate),
    1,
    limit - match - 2,
  );
  const hintEnd = clamp(
    Math.round((fits ? LOOP_HINT_END : duration * 0.95) * rate),
    hintStart + match + 1,
    limit,
  );

  let start = hintStart;
  for (let d = 0; d <= search; d++) {
    if (hintStart + d < limit && rising(hintStart + d)) {
      start = hintStart + d;
      break;
    }
    if (hintStart - d > 0 && rising(hintStart - d)) {
      start = hintStart - d;
      break;
    }
  }

  let end = hintEnd;
  let best = Infinity;
  const lo = Math.max(start + match + 1, hintEnd - search);
  const hi = Math.min(limit, hintEnd + search);
  for (let candidate = lo; candidate <= hi; candidate++) {
    if (!rising(candidate)) continue;
    let cost = 0;
    for (let k = 0; k < match; k++) {
      const d = data[start + k] - data[candidate + k];
      cost += d * d;
    }
    if (cost < best) {
      best = cost;
      end = candidate;
    }
  }
  if (end <= start) return wholeBuffer;
  return { start: start / rate, end: end / rate };
}

/**
 * Finds where the start signal's final tone begins, in seconds.
 *
 * Loud stretches are found with a hysteresis threshold relative to the clip's own peak,
 * stretches closer together than `CUE_MERGE_GAP` are merged, short ones are discarded,
 * and the last survivor's onset is the cue. The merge is not optional: the tone in the
 * supplied recording dips below the threshold for one 5 ms window as it decays, which
 * without merging reads as a separate 25 ms "beep" at the very end and would put the
 * green light a second late.
 *
 * Falls back to aligning the *end* of the clip with the green light, which is the right
 * guess for a countdown whose structure we cannot read.
 */
export function findFinalTone(data: Float32Array, rate: number): number {
  const duration = data.length / rate;
  const win = Math.max(1, Math.round(CUE_WINDOW * rate));
  if (data.length < win * 8) return duration;

  let peak = 0;
  for (let i = 0; i < data.length; i++) {
    const v = Math.abs(data[i]);
    if (v > peak) peak = v;
  }
  if (peak <= 0) return duration;

  const onLevel = peak * Math.pow(10, CUE_ON_DB / 20);
  const offLevel = peak * Math.pow(10, CUE_OFF_DB / 20);

  const spans: { start: number; end: number }[] = [];
  let loud = false;
  for (let s = 0; s + win <= data.length; s += win) {
    let sum = 0;
    for (let i = s; i < s + win; i++) sum += data[i] * data[i];
    const rms = Math.sqrt(sum / win);
    if (!loud && rms > onLevel) {
      spans.push({ start: s / rate, end: (s + win) / rate });
      loud = true;
    } else if (loud) {
      spans[spans.length - 1].end = (s + win) / rate;
      if (rms < offLevel) loud = false;
    }
  }

  const merged: { start: number; end: number }[] = [];
  for (const span of spans) {
    const last = merged[merged.length - 1];
    if (last && span.start - last.end < CUE_MERGE_GAP) last.end = span.end;
    else merged.push({ ...span });
  }

  const real = merged.filter((s) => s.end - s.start >= CUE_MIN_LENGTH);
  return real.length ? real[real.length - 1].start : duration;
}

/** Preferred container first. Empty string from `canPlayType` means "definitely not". */
function orderedFormats(): string[] {
  try {
    const probe = document.createElement('audio');
    if (!probe.canPlayType) return ['ogg', 'm4a'];
    const ogg = probe.canPlayType('audio/ogg; codecs="opus"');
    const m4a = probe.canPlayType('audio/mp4; codecs="mp4a.40.2"');
    if (ogg && !m4a) return ['ogg'];
    if (m4a && !ogg) return ['m4a'];
    if (ogg === 'probably' && m4a !== 'probably') return ['ogg', 'm4a'];
    if (m4a === 'probably' && ogg !== 'probably') return ['m4a', 'ogg'];
    return ['ogg', 'm4a'];
  } catch {
    return ['ogg', 'm4a'];
  }
}

function readMuted(): boolean {
  try {
    return localStorage.getItem(MUTE_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * A stable per-car pitch offset from the player id, in cents — see `DETUNE_SPREAD`. FNV-1a
 * rather than anything cryptographic: this only has to spread ids apart, not resist being
 * guessed, and it needs to be the same number every frame without storing anything.
 */
export function detuneFor(id: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < id.length; i++) {
    hash ^= id.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  const unit = (hash >>> 0) / 0xffffffff; // 0..1
  return (unit * 2 - 1) * DETUNE_SPREAD;
}

/**
 * Rotates a vector by a quaternion — `v + 2·(q.w·t + q.xyz × t)` where `t = q.xyz × v`, the
 * standard formula. Takes plain numbers rather than a `THREE.Quaternion` so this file has
 * no rendering dependency; `updateListener` uses it to turn the camera's quaternion into
 * the forward/up vectors `AudioListener` wants.
 */
export function rotateVector(
  qx: number,
  qy: number,
  qz: number,
  qw: number,
  vx: number,
  vy: number,
  vz: number,
): { x: number; y: number; z: number } {
  const tx = 2 * (qy * vz - qz * vy);
  const ty = 2 * (qz * vx - qx * vz);
  const tz = 2 * (qx * vy - qy * vx);
  return {
    x: vx + qw * tx + (qy * tz - qz * ty),
    y: vy + qw * ty + (qz * tx - qx * tz),
    z: vz + qw * tz + (qx * ty - qy * tx),
  };
}

/**
 * One instance for the page. The `Game` is torn down and rebuilt whenever the host picks a
 * different region, and re-decoding 130 kB of audio each time — or worse, leaking a second
 * `AudioContext`, of which browsers allow very few — would be silly.
 */
export const carAudio = new CarAudio();

// `M` may have already been pressed in a previous session — `carAudio`'s own mute state is
// restored from storage in its field initialiser, but `musicPlayer` is a separate object
// that has no way to know that happened. One line keeps them starting in agreement.
musicPlayer.setMuted(carAudio.muted);
